/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {useFilePermissionsSearch} from '@ion/applications/src/hooks/useFilePermissionsSearch';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFileFilters, GenericObject} from '@ion/core/src/types/entities';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  INVALID,
  LookUpSource,
  Mapping,
  SuggestionsType,
  VALID
} from '@ion/features/src/search/types';
import {createModularSearchFilters, updateDeviceFileSearchQueries} from '../../../store/actions/devicefiles';
import {deviceFileSearch} from '../../../store/selectors/device-files';

export enum SearchFields {
  NAME = 'name',
  TYPE = 'platformId',
  FILE_VERSION = 'version',
  STATUS = 'status',
  APP_ID = 'appId'
};

// Defines the searchable attributes, options, and labels for modularized search.
const DeviceFileMappings = (Features, filePermissions): {[key: string]: Mapping} => ({
  [SearchFields.NAME] : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('devicefiles:modularSearch.labels.name'),
    key        : SearchFields.NAME
  },
  [SearchFields.TYPE] : {
    type       : AttributeTypes.LOOKUP,
    attributes : {
      value : AttributeOperators.IS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion        : SuggestionsType.LOOKUP,
    suggestionsSource : LookUpSource.DeviceFiles,
    label             : translate('devicefiles:modularSearch.labels.type'),
    key               : SearchFields.TYPE,
    isTreeView        : true,
    localeValues      : {
      root     : translate('devicefiles:modularSearch.labels.nestedlabels.root'),
      children : translate('devicefiles:modularSearch.labels.nestedlabels.children')
    },
    hasFeatures       : Features,
    hasFilePermission : filePermissions
  },
  [SearchFields.FILE_VERSION] : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion          : SuggestionsType.TEXT,
    label               : translate('devicefiles:modularSearch.labels.fileVersion'),
    key                 : SearchFields.FILE_VERSION,
    hasNoCharacterLimit : true,
    hasPartialSubString : true,
    hasExactMatch       : true
  },
  [SearchFields.STATUS] : {
    type       : AttributeTypes.SELECT,
    attributes : {
      value : AttributeOperators.IS,
      label : translate('common:modularsearch.attributes.is')
    },
    options : [
      {
        label : translate('devicefiles:modularSearch.status.valid'),
        value : VALID.toUpperCase()
      },
      {
        label : translate('devicefiles:modularSearch.status.invalid'),
        value : INVALID.toUpperCase()
      }
    ],
    suggestion : SuggestionsType.SELECTION,
    label      : translate('devicefiles:modularSearch.labels.status'),
    key        : SearchFields.STATUS
  },
  [SearchFields.APP_ID] : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion          : SuggestionsType.TEXT,
    label               : translate('devicefiles:modularSearch.labels.appId'),
    key                 : SearchFields.APP_ID,
    hasNoCharacterLimit : true,
    hasNumericValue     : true,
    hasPartialSubString : true,
    hasExactMatch       : true
  }
});

// Defines the potential search types for modularized search.
const uniqueConditions = (): string => {
  const {is, contains} = translate('common:modularsearch.attributes', {returnObjects: true});
  return (`(${contains}|${is})`);
};

const getMemberSearchFieldCriteria = (accountId, filters, viewSubGroups) => ({
  searchCriteria : [{
    memberSearch : {
      hierLevel   : viewSubGroups ? 'ALL' : '',
      searchTerms : {
        account : accountId,
        ...createModularSearchFilters(filters)
      }
    }
  }]
});

export const useModularSearch = (onRefresh?: Function, Features?: GenericObject, viewSubGroups?: boolean) => {
  const dispatch              = useDispatch();
  const currentGlobalGroupId  = useCurrentGlobalGroupId();
  const hasReadFilePermission = useFilePermissionsSearch(Features);
  const search                = useSelector(deviceFileSearch);

  // Dispatches the given search params to the search redux state for modularized search.
  const getSearchParams = useCallback(function(filters: DeviceFileFilters) {
    dispatch(updateDeviceFileSearchQueries(filters));
    onRefresh();
  }, [onRefresh]);

  const mappings = useMemo(() => {
    return new BaseSearchMapping(
      DeviceFileMappings(Features, hasReadFilePermission),
      uniqueConditions(),
      currentGlobalGroupId
    );
  }, [Features, hasReadFilePermission, currentGlobalGroupId]);

  const filtersSearchCriteria = getMemberSearchFieldCriteria(currentGlobalGroupId, search, viewSubGroups);

  return {getSearchParams, mappings, filtersSearchCriteria};
};

export default useModularSearch;
